/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const { Term, BinaryOperator, Locale } = require('@cedalo/parser');
const parse = require('date-fns/parse');
const isValid = require('date-fns/isValid');
const de = require('date-fns/locale/de').default;
const enGB = require('date-fns/locale/en-GB').default;
const enUS = require('date-fns/locale/en-US').default;

const JSG = require('../../JSG');
const Expression = require('../expr/Expression');
const ItemAttributes = require('../attr/ItemAttributes');
const NotificationCenter = require('../notifications/NotificationCenter');
const Notification = require('../notifications/Notification');
const Rectangle = require('../../geometry/Rectangle');
const Point = require('../../geometry/Point');
const GraphUtils = require('../GraphUtils');

const RowHeaderNode = require('./RowHeaderNode');
const CellsNode = require('./CellsNode');
const ContentNode = require('./ContentNode');
const LayoutNode = require('./LayoutNode');
const CellRange = require('./CellRange');
const ColumnHeaderNode = require('./ColumnHeaderNode');
const SheetHeaderNode = require('./SheetHeaderNode');
const SheetReference = require('../parser/SheetReference');
const Selection = require('./Selection');
const ExpressionHelper = require('../expr/ExpressionHelper');
const SheetModel = require('./SheetModel');
const FormatAttributes = require('../attr/FormatAttributes');

const SelectionMode = {
	CELL: 0,
	COLUMN: 1,
	ROW: 2
};
const locales = { en: enGB, enUS, de };
const dateFormats = [
	'dd.MM.yy',
	'dd.MM.yy HH:mm',
	'dd.MM.yyyy HH:mm',
	'dd.MM.yyyy',
	'dd. MMM yyyy HH:mm',
	'dd. MMM yy',
	'dd. MMM yyyy',
	'dd. MMM',
	'MMM yy',
	'MMM yyyy',
	'HH:mm',
	'HH:mm:ss',
	'HH:mm:ss.SSS'
];

const SELECTION_CHANGED_NOTIFICATION = 'sheet_selection_changed_notification';

/**
 * Node representing a worksheet. The worksheet con tains additional nodes for the rows,
 * columns, cells and the top left corner.
 *
 * @class WorksheetNode
 * @extends ContentNode
 * @constructor
 */
module.exports = class WorksheetNode extends ContentNode {
	constructor(model) {
		super();

		this._model = model || new SheetModel();
		this._editBarMode = 'ref';
		// this._selection = new Selection(this._model);

		this.getItemAttributes().setSelectionMode(ItemAttributes.SelectionMode.NONE);
		this.getItemAttributes().setPortMode(ItemAttributes.PortMode.NONE);
		this.getItemAttributes().setClipChildren(true);
		this.getItemAttributes().setContainer(false);
		this.getFormat().setLineCorner(75);
		this.getFormat().setLineColor(JSG.theme.frame);

		this._cells = this.addItem(new CellsNode(this._model));
		this._columns = this.addItem(new ColumnHeaderNode(this._model.columns));
		this._rows = this.addItem(new RowHeaderNode(this._model.rows));
		this._corner = this.addItem(new SheetHeaderNode());
		this._corner.getFormat().setFillStyle(FormatAttributes.FillStyle.PATTERN);
		this._corner.getFormat().setPatternStyle(FormatAttributes.PatternStyle.STRETCHPROPORTIONAL);
		this._corner.getFormat().setPattern('info');
		this._corner.getFormat().setShadowOffsetX(1);
		this._corner.setTooltip(`<p style="font-weight: bold;">${JSG.getLocalizedString('CalcInfoHeader')}</p>` +
			`<p>${JSG.getLocalizedString('CalcInfoRow1')}</br>${JSG.getLocalizedString('CalcInfoRow2')}</p>`);
	}

	newInstance() {
		return new WorksheetNode();
	}

	get properties() {
		return this._properties;
	}

	set model(model) {
		this._model = model;
		this._cells._model = model;
		this._columns.sectionData = model.columns;
		this._rows.sectionData = model.rows;
	}

	get model() {
		return this._model;
	}

	get selection() {
		return this._model.selection;
	}

	getOwnSelection() {
		const graph = this.getGraph();
		return graph ? graph.sheetSelection : undefined;
	}

	setSelection(id, data) {
		super.setSelection(id, data);
	}

	getLayoutNode() {
		let ret;

		this._cells.subItems.some(subItem => {
			if (subItem instanceof LayoutNode) {
				ret = subItem;
				return true;
			}
			return false;
		});

		return ret;
	}

	removeSelection(id) {
		super.removeSelection(id);
		const mySelectionId = String(this.getSelectionId());
		if (id === mySelectionId) {
			this.getOwnSelection().clear();
		}

		const layoutNode = this.getLayoutNode();
		if (layoutNode) {
			GraphUtils.traverseItem(layoutNode, item => {
				if (item instanceof WorksheetNode) {
					item.removeSelection();
				}
			}, false);
		}

	}

	/**
	 * Layout process sheet container items.
	 *
	 * @method layout
	 */
	layout() {
		const header = this.isHeaderVisible();
		const layoutNode = this.getLayoutNode();

		this._corner.getItemAttributes().setVisible(header);
		this._rows.getItemAttributes().setVisible(header);
		this._columns.getItemAttributes().setVisible(header);

		const colSize = layoutNode ? new Point(0, 0) : this._columns.getInternalSize();
		const rowSize = layoutNode ? new Point(0, 0) : this._rows.getInternalSize();
		const box = JSG.boxCache.get();

		if (header) {
			box.setLeft(rowSize.x);
			box.setTop(0);
			box.setWidth(colSize.x);
			box.setHeight(colSize.y);

			this._columns.setBoundingBoxTo(box);

			box.setLeft(0);
			box.setTop(colSize.y);
			box.setWidth(rowSize.x);
			box.setHeight(rowSize.y);

			this._rows.setBoundingBoxTo(box);

			box.setLeft(rowSize.x);
			box.setTop(colSize.y);
			box.setWidth(colSize.x);
			box.setHeight(rowSize.y);

			this._cells.setBoundingBoxTo(box);

			box.setLeft(0);
			box.setTop(0);
			box.setWidth(rowSize.x);
			box.setHeight(colSize.y);

			if (rowSize.x === 0 || colSize.y === 0) {
				this._corner.getItemAttributes().setVisible(false);
			} else {
				this._corner.getItemAttributes().setVisible(true);
				this._corner.setBoundingBoxTo(box);
			}
		} else {
			box.setLeft(0);
			box.setTop(0);
			box.setWidth(colSize.x);
			box.setHeight(rowSize.y);

			this._cells.setBoundingBoxTo(box);
		}

		JSG.boxCache.release(box);

		super.layout();
	}

	_copy(copiednodes, deep, ids) {
		const copy = super._copy(copiednodes, deep, ids);

		copy.model = this.model.copy();
		copy._assignItems();

		return copy;
	}

	_assignItems() {
		let i;
		const cp = this.getContentPane();

		for (i = 0; i < cp.getItemCount(); i += 1) {
			const item = cp.getItemAt(i);
			if (item instanceof CellsNode) {
				this._cells = item;
			} else if (item instanceof ColumnHeaderNode) {
				this._columns = item;
			} else if (item instanceof RowHeaderNode) {
				this._rows = item;
			} else if (item instanceof SheetHeaderNode) {
				this._corner = item;
			}
		}
	}

	isAddLabelAllowed() {
		return false;
	}

	isShowFormulas() {
		return this.model.showFormulas;
	}

	setShowFormulas(flag) {
		this.model.showFormulas = flag;
		this.model.columns.showFormulas = flag;
	}

	isGridVisible() {
		const graph = this.graph;

		if (graph && graph.showViewMode) {
			return graph.viewSettings.showGrid;
		}

		return this.model.showGrid;
	}

	setGridVisible(flag) {
		this.model.showGrid = flag;
	}

	isHeaderVisible() {
		const graph = this.graph;

		if (graph && graph.showViewMode) {
			return graph.viewSettings.showHeader;
		}
		return this.model.showHeader;
	}

	isProtected() {
		const graph = this.getGraph();

		if (graph !== undefined) {
			if (graph.overrideProtection) {
				return false;
			}
			const view = graph.viewSettings;
			if (view.active === true) {
				return true;
			}
		}
		return this.model.protected;
	}

	getProtected() {
		return this.model.protected;
	}

	setProtected(flag) {
		this.model.protected = flag;
	}

	getColumns() {
		return this._columns;
	}

	getRows() {
		return this._rows;
	}

	getCorner() {
		return this._corner;
	}

	get cells() {
		return this.model.cells;
	}

	getCells() {
		return this._cells;
	}

	getItemType() {
		return 'worksheetnode';
	}

	toJSON(sheet, cb) {
		const ret = super.toJSON(sheet, cb);

		ret.worksheetattributes = {
			protected: this.model.protected,
			showHeader: this.model.showHeader,
			showGrid: this.model.showGrid,
			showFormulas: this.model.showFormulas,
		};

		return ret;
	}

	fromJSON(json) {
		super.fromJSON(json);

		if (!json.worksheetattributes ||
			(json.worksheetattributes && json.worksheetattributes.showGrid === undefined)) {
			// read from old properties
			this.model.protected = false;
			this.model.showFormulas = false;
			this.model.showGrid = false;
			this.model.showHeader = false;
		} else {
			this.model.protected = json.worksheetattributes.protected;
			this.model.showFormulas = json.worksheetattributes.showFormulas;
			this.model.showGrid = json.worksheetattributes.showGrid;
			this.model.showHeader = json.worksheetattributes.showHeader;
		}
		this.sourceToModel();
	}

	sourceToModel() {
		const sourceAttr = this.getAttributeAtPath('range');
		if (!sourceAttr) {
			return;
		}
		const expr = sourceAttr.getExpression();
		if (!expr) {
			return;
		}

		expr.evaluateForSheet(this.model);
		let range;
		const term = expr.getTerm();
		if (!term) {
			return;
		}
		if (term.operand && term.operand instanceof SheetReference) {
			range =  term.operand.getRange().copy();
		} else {
			const rangeString = sourceAttr.getValue();
			if (rangeString !== undefined) {
				range = CellRange.parse(rangeString, this);
			}
		}

		if (!range) {
			return;
		}

		range.shiftFromSheet();
		this.model.cells = range.sheet.cells;
		this.model.rows = range.sheet.rows;
		this.model.columns = range.sheet.columns;
		this._columns.sectionData = this.model.columns;
		this._columns.viewRange = range;
		this._rows.sectionData = this.model.rows;
		this._rows.viewRange = range;
		this.model.name = range.sheet.name;
		this.model.id = range.sheet.id;

	}

	saveContent(writer, absolute) {

		super.saveContent(writer, absolute);

		writer.writeStartElement('properties');
		writer.writeAttributeNumber('showgrid', this._model.showGrid ? 1 : 0);
		writer.writeAttributeNumber('showformulas', this._model.showFormulas ? 1 : 0);
		writer.writeAttributeNumber('greyifrows', this._model.greyIfRows ? 1 : 0);
		writer.writeAttributeNumber('showheader', this._model.showHeader ? 1 : 0);
		writer.writeAttributeNumber('protected', this._model.protected ? 1 : 0);
		writer.writeEndElement();

		writer.writeStartElement('defaultcell');
		this.model.defaultCell.save(writer);
		writer.writeEndElement();
	}

	_assignName(id) {
		this.setName(`Worksheet${id}`);
	}

	read(reader, object) {
		super.read(reader, object);

		const properties = reader.getObject(object, 'properties');
		if (properties !== undefined) {
			this.model.showGrid = reader.getAttributeBoolean(properties, 'showgrid', true);
			this.model.showHeader = reader.getAttributeBoolean(properties, 'showheader', true);
			this.model.showFormulas = reader.getAttributeBoolean(properties, 'showformulas', false);
			this.model.greyIfRows = reader.getAttributeBoolean(properties, 'greyifrows', true);
			this.model.protected = reader.getAttributeBoolean(properties, 'protected', false);
		}
		const defaultCell = reader.getObject(object, 'defaultcell');
		if (defaultCell !== undefined) {
			const cell = reader.getObject(defaultCell, 'cell');
			this.model.defaultCell.read(reader, cell);
		}

		this._assignItems();
		this.sourceToModel();
	}


	evaluate() {
		super.evaluate(this);
		this.cells.evaluate(this);
	}

	invalidateTerms() {
		this.cells.invalidateTerms();
		super.invalidateTerms(this);
	}

	getRangeFromPositions(cellStart, cellEnd) {
		let top;
		let left;
		let right;
		let bottom;

		if (cellStart.x === -1) {
			left = 0;
			right = this._columns.getSections() - 1;
		} else {
			left = cellStart.x;
			if (cellEnd !== undefined) {
				if (cellEnd.x === -1) {
					left = 0;
					right = this._columns.getSections() - 1;
				} else {
					right = cellEnd.x;
				}
			}
		}

		if (cellStart.y === -1) {
			top = 0;
			bottom = this._rows.getSections() - 1;
		} else {
			top = cellStart.y;
			if (cellEnd !== undefined) {
				if (cellEnd.y === -1) {
					top = 0;
					bottom = this._rows.getSections() - 1;
				} else {
					bottom = cellEnd.y;
				}
			}
		}

		return new CellRange(this.model, left, top, right, bottom);
	}

	getCellRect(range) {
		const rect = new Rectangle(0, 0, 0, 0);

		rect.x = this._columns.getSectionPos(range.getX1());
		rect.y = this._rows.getSectionPos(range.getY1());
		rect.width = this._columns.getSectionPos(range.getX2() + 1) - rect.x;
		rect.height = this._rows.getSectionPos(range.getY2() + 1) - rect.y;

		return rect;
	}

	isCellProtected(c, r) {
		if (this.isProtected()) {
			if (c === -1 || r === -1) {
				return true;
			}
			const attr = this.model.getCellProperties(c, r, true);
			return attr.protected;
		}

		return false;
	}

	isFormulaHidden(c, r) {
		if (this.isProtected()) {
			if (c === -1 || r === -1) {
				return true;
			}
			const attr = this.getCellPropertiesRC(c, r);
			return !!(attr && attr.getVisible().getValue());
		}

		return false;
	}

	getNextSelectableColumn(index, row) {
		const max = this.model.maxColumns;
		let newIndex = index + 1;

		while (newIndex < max && (this.model.columns.getSectionSize(newIndex) === 0 || this.isCellProtected(newIndex, row))) {
			newIndex += 1;
		}

		return newIndex < max ? newIndex : index;
	}

	getPreviousSelectableColumn(index, row) {
		let newIndex = index - 1;

		while (newIndex >= 0 && (this.model.columns.getSectionSize(newIndex) === 0 || this.isCellProtected(newIndex, row))) {
			newIndex -= 1;
		}

		return newIndex >= 0 ? newIndex : index;
	}

	getNextSelectableRow(index, column) {
		const max = this.model.maxRows;
		let newIndex = index + 1;

		while (newIndex < max && (this.model.rows.getSectionSize(newIndex) === 0 || this.isCellProtected(column, newIndex))) {
			newIndex += 1;
		}

		return newIndex < max ? newIndex : index;
	}

	getPreviousSelectableRow(index, column) {
		let newIndex = index - 1;

		while (newIndex >= 0 && (this.model.rows.getSectionSize(newIndex) === 0 || this.isCellProtected(column, newIndex))) {
			newIndex -= 1;
		}

		return newIndex >= 0 ? newIndex : index;
	}

	/**
	 * Retrieve next cell, that contains a value, if given cell is empty or does not contain a value, if given cell
	 * is filled
	 * @param {Point) pos Cell location to start from.
	 * @param {String} direction Direction search as string ('left', 'up', 'right' 'bottom')
	 * @returns {number} Index of identified cell.
	 */
	getNextOrLastUsedCell(pos, direction) {
		let i;
		let rowdata;
		let cell;
		let used;
		let max;
		let last;
		const cells = this.model.cells;

		switch (direction) {
		case 'left':
			cell = cells.getRC(this.getPreviousSelectableColumn(pos.x, pos.y), pos.y);
			used = cell === undefined || cell.getExpression() === undefined;
			rowdata = cells.getRow(pos.y);
			i = pos.x;
			while (i >= 0) {
				last = i;
				i = this.getPreviousSelectableColumn(i, pos.y);
				if (i === last) {
					return i;
				}
				if (rowdata) {
					const coldata = rowdata[i];
					if (used) {
						if (coldata && coldata.getExpression()) {
							return i;
						}
					} else if (!coldata || !coldata.getExpression()) {
						return this.getNextSelectableColumn(i, pos.y);
					}
				}
			}
			return 0;
		case 'right':
			max = this.model.maxColumns + 1;
			cell = cells.getRC(this.getNextSelectableColumn(pos.x, pos.y), pos.y);
			used = cell === undefined || cell.getExpression() === undefined;
			rowdata = cells.getRow(pos.y);
			i = pos.x;
			while (i < max) {
				last = i;
				i = this.getNextSelectableColumn(i, pos.y);
				if (i === last) {
					return i;
				}
				if (rowdata) {
					const coldata = rowdata[i];
					if (used) {
						if (coldata && coldata.getExpression()) {
							return i;
						}
					} else if (!coldata || !coldata.getExpression()) {
						return this.getPreviousSelectableColumn(i, pos.y);
					}
				}
			}
			return max;
		case 'up':
			cell = cells.getRC(pos.x, this.getPreviousSelectableRow(pos.y, pos.x));
			used = cell === undefined || cell.getExpression() === undefined;
			i = pos.y;
			while (i >= 0) {
				last = i;
				i = this.getPreviousSelectableRow(i, pos.x);
				if (i === last) {
					return i;
				}
				rowdata = cells.getRow(i);
				if (used) {
					if (rowdata) {
						const coldata = rowdata[pos.x];
						if (coldata && coldata.getExpression()) {
							return i;
						}
					}
				} else if (rowdata) {
					const coldata = rowdata[pos.x];
					if (!coldata || !coldata.getExpression()) {
						return this.getNextSelectableRow(i, pos.x);
					}
				} else {
					return this.getNextSelectableRow(i, pos.x);
				}
			}
			return 0;
		case 'down':
			max = this.model.maxRows + 1;
			cell = cells.getRC(pos.x, this.getNextSelectableRow(pos.y, pos.x));
			used = cell === undefined || cell.getExpression() === undefined;
			i = pos.y;
			while (i < max) {
				last = i;
				i = this.getNextSelectableRow(i, pos.x);
				if (i === last) {
					return i;
				}
				rowdata = cells.getRow(i);
				if (used) {
					if (rowdata) {
						const coldata = rowdata[pos.x];
						if (coldata && coldata.getExpression()) {
							return i;
						}
					}
				} else if (rowdata) {
					const coldata = rowdata[pos.x];
					if (!coldata || !coldata.getExpression()) {
						return this.getPreviousSelectableRow(i, pos.x);
					}
				} else {
					return this.getPreviousSelectableRow(i, pos.x);
				}
			}
			return this.model.maxColumns - 1;
		}

		return 0;
	}


	getCustomReference(property) {
		// const ref = new SheetReference(this.model, property);
		//
		// return ref.isValid() ? ref : Reference.INVALID;
	}

	textToExpression(text) {
		const isFormula = text.charAt(0) === '=';
		let term;
		let localCulture;
		let numberFormat;

		//
		// if (text.length === 0) {
		// 	return {
		// 		expression,,
		// 		localCulture,
		// 		numberFormat
		// 	};
		// }

		const cell = this.getOwnSelection().getActiveCell();
		if (cell !== undefined) {
			// DL-4076: text handling like in excel
			const asText = text.charAt(0) === "'";
			const type = this.model.getTextFormatProperties(cell.x, cell.y, true).localculture;
			if ((type && type === 'text') || asText) {
				if (asText) text = text.substring(1);
				return {
					expression: ExpressionHelper.createExpressionFromValueTerm(Term.fromString(text))
				};
			}
		}

		if (isFormula) {
			text = text.substring(1);
		}

		JSG.FormulaParser.context.separators = JSG.getParserLocaleSettings().separators;

		if (isFormula) {
			term = JSG.FormulaParser.parseSheet(text, this.model);
		} else {
			const locale = JSG.getParserLocale();
			let date;
			dateFormats.some((format) => {
				date = parse(text, format, new Date(), { locale: locales[locale] });
				if (isValid(date)) {
					let xlDate = this.cells.JSDateToExcelDate(date);
					switch (format) {
						case 'dd.MM.yy':
							xlDate = Math.floor(xlDate);
							localCulture = `date;${locale}`;
							numberFormat = 'dd\\.mm\\.yy';
							break;
						case 'dd.MM.yy HH:mm':
							localCulture = `date;${locale}`;
							numberFormat = 'd\\.m\\.yy h:mm';
							break;
						case 'dd.MM.yyyy HH:mm':
							localCulture = `date;${locale}`;
							numberFormat = 'd\\.m\\.yyyy h:mm';
							break;
						case 'dd.MM.yyyy':
							xlDate = Math.floor(xlDate);
							localCulture = `date;${locale}`;
							numberFormat = 'dd\\.mm\\.yyyy';
							break;
						case 'dd. MMM yyyy HH:mm':
							localCulture = `date;${locale}`;
							numberFormat = 'd\\.m\\.yy h:mm';
							break;
						case 'dd. MMM yy':
						case 'dd. MMM yyyy':
							xlDate = Math.floor(xlDate);
							localCulture = `date;${locale}`;
							numberFormat = 'd\\. mmm yyyy';
							break;
						case 'dd. MMM':
							xlDate = Math.floor(xlDate);
							localCulture = `date;${locale}`;
							numberFormat = 'd\\. mmm';
							break;
						case 'MMM yy':
						case 'MMM yyyy':
							xlDate = Math.floor(xlDate);
							localCulture = `date;${locale}`;
							numberFormat = 'mmm yy';
							break;
						case 'HH:mm':
							xlDate -= Math.trunc(xlDate);
							localCulture = `time;${locale}`;
							numberFormat = 'h:mm';
							break;
						case 'HH:mm:ss':
							xlDate -= Math.trunc(xlDate);
							localCulture = `time;${locale}`;
							numberFormat = 'h:mm:ss';
							break;
						case 'HH:mm:ss.SSS':
							xlDate -= Math.trunc(xlDate);
							localCulture = `time;${locale}`;
							numberFormat = 'h:mm:ss.000';
							break;
						default:
							xlDate = Math.floor(xlDate);
							localCulture = `date;${locale}`;
							numberFormat = 'dd\\.mm\\.yy';
							break;
					}
					term = Term.fromNumber(xlDate);
					return true;
				}
				return false;
			});
			if (term === undefined) {
				term = JSG.FormulaParser.parseValue(text, this.model);
			}
		}

		JSG.FormulaParser.context.separators = Locale.EN.separators;

		const formula = term ? term.toLocaleString('en', { sheet: this.model, useName: true, forceName: true }) : '';
		const expr = isFormula ? new Expression(0, formula) : ExpressionHelper.createExpressionFromValueTerm(term);

		if (term) {
			let unit;
			term.traverse((lterm) => {
				if (
					!(lterm.operand && lterm.operand.type === 'number') &&
					!(lterm.operator instanceof BinaryOperator)
				) {
					unit = unit === undefined && lterm.isUnit ? true : unit && lterm.isUnit;
				}
				return true;
			});
			if (unit) {
				localCulture = `percent`;
				if (Number.isInteger(term.value * 100)) {
					numberFormat = '0%';
				} else {
					numberFormat = '0.00%';
				}
			}
		}

		return {
			expression: expr,
			term,
			localCulture,
			numberFormat
		};
	}

	collectDependants() {
		const pos = this.getOwnSelection().getActiveCell();
		if (!pos) {
			return;
		}

		const cells = [];

		JSG.streamSheets.forEach(container => {
			const data = container.sheet.cells;
			const shift =  container.sheet.colOffset

			data.enumerate((column, row, cell) => {
				const term = cell.getTerm();
				if (term) {
					term.traverse((lterm) => {
						const { operand } = lterm;
						if (operand && operand instanceof SheetReference) {
							if (operand._range) {
								let range = operand._range;
								range = range.copy();
								if (range.sheet === this.model &&
									pos.x >= range._x1 - shift && pos.x <= range._x2 - shift &&
									pos.y >= range._y1 && pos.y <= range._y2) {
									cells.push(new CellRange(container.sheet, column, row));
								}
							}
						}
						return true;
					});
				}
			});
		});

		if (cells.length) {
			if (!this.dependants) {
				this.dependants = [];
			}
			this.dependants.push({
				cell: new CellRange(this.model, pos.x, pos.y),
				cells
			});
		}
	}

	collectPredecessors() {
		const pos = this.getOwnSelection().getActiveCell();
		if (!pos) {
			return;
		}
		const data = this.cells;
		const cell = data.get(pos);
		if (!cell) {
			return;
		}

		const term = cell.getTerm();
		if (!term) {
			return;
		}

		const cells = [];

		term.traverse((lterm) => {
			const { operand } = lterm;
			if (operand && operand instanceof SheetReference) {
				if (operand._range) {
					const range = operand._range;
					cells.push(range.copy());
				}
			}
			return true;
		});

		if (cells.length) {
			if (!this.predecessors) {
				this.predecessors = [];
			}

			this.predecessors.push({
				cell: new CellRange(this.model, pos.x, pos.y),
				cells
			});
		}
	}

	removeArrows() {
		this.predecessors = undefined;
		this.dependants = undefined;
	}

	static get SelectionMode() {
		return SelectionMode;
	}

	static get SELECTION_CHANGED_NOTIFICATION() {
		return SELECTION_CHANGED_NOTIFICATION;
	}
};
